import * as THREE from "three";
import { Vector3 } from "three";

const _bind = (scope, fn) => {
  return function () {
    fn.apply(scope, arguments);
  };
};

const mapLinear = (
  x: number,
  a1: number,
  a2: number,
  b1: number,
  b2: number
): number => {
  return b1 + ((x - a1) * (b2 - b1)) / (a2 - a1);
};
const degToRad = (val: number) => val * (Math.PI / 180.0);
const clamp = (num: number, min: number, max: number): number =>
  Math.min(Math.max(num, min), max);

/**
 * FirstPersonControls class
 *
 * @author mrdoob / http://mrdoob.com/
 * @author alteredq / http://alteredqualia.com/
 * @author paulirish / http://paulirish.com/
 */
class FirstCameraControl {
  /**
   * Constructor
   * @param  {THREE.Camera} camera  THREE.Camera
   */
  camera: THREE.Camera;
  target: THREE.Vector3;
  movementSpeed: number;
  lookSpeed: number;
  lookVertical: boolean;
  autoForward: boolean;
  activeLook: boolean;
  heightSpeed: boolean;
  heightCoef: number;
  canvasElement: HTMLCanvasElement;
  heightMin: number;
  heightMax: number;

  enabled: boolean;

  constrainVertical: boolean;
  verticalMin: number;
  verticalMax: number;

  autoSpeedFactor: number;
  mouseX: number;
  mouseY: number;

  lat: number;
  lon: number;
  phi: number;
  theta: number;

  moveForward: boolean;
  moveBackward: boolean;
  moveLeft: boolean;
  moveRight: boolean;

  mouseDragOn: boolean;
  moveUp: boolean;
  moveDown: boolean;

  viewHalfX: number;
  viewHalfY: number;

  minClampPosition: Vector3 | undefined;
  maxClampPosition: Vector3 | undefined;

  constructor(
    camera: THREE.Camera,
    canvasElement: HTMLCanvasElement,
    minClampPosition: Vector3 | undefined = undefined,
    maxClampPosition: Vector3 | undefined = undefined
  ) {
    this.camera = camera;
    this.canvasElement = canvasElement;

    this.target = new THREE.Vector3(0, 0, 0);
    this.enabled = true;
    this.movementSpeed = 0;
    this.lookSpeed = 0;

    this.lookVertical = true;
    this.autoForward = false;

    this.activeLook = true;

    this.heightSpeed = false;
    this.heightCoef = 1.0;
    this.heightMin = 0.0;
    this.heightMax = 1.0;

    this.constrainVertical = true;
    this.verticalMin = 0;
    this.verticalMax = Math.PI;

    this.autoSpeedFactor = 0.0;

    this.mouseX = 0;
    this.mouseY = 0;

    this.lat = 0;
    this.lon = 0;
    this.phi = 0;
    this.theta = 0;

    this.moveForward = false;
    this.moveBackward = false;
    this.moveLeft = false;
    this.moveRight = false;

    this.mouseDragOn = false;
    this.moveUp = false;
    this.moveDown = false;
    this.viewHalfX = 0;
    this.viewHalfY = 0;
    // this.canvasElement.setAttribute('tabindex', "-1.0");
    this.canvasElement.addEventListener(
      "contextmenu",
      function (event) {
        event.preventDefault();
      },
      false
    );
    this.canvasElement.addEventListener(
      "mousemove",
      _bind(this, this.onMouseMove),
      false
    );
    this.canvasElement.addEventListener(
      "mousedown",
      _bind(this, this.onMouseDown),
      false
    );
    this.canvasElement.addEventListener(
      "mouseup",
      _bind(this, this.onMouseUp),
      false
    );

    window.addEventListener("keydown", _bind(this, this.onKeyDown), false);
    window.addEventListener("keyup", _bind(this, this.onKeyUp), false);
    this.handleResize();
    // Set Position Clamps
    if (typeof minClampPosition != "undefined")
      this.minClampPosition = minClampPosition;
    if (typeof maxClampPosition != "undefined")
      this.maxClampPosition = maxClampPosition;
  }

  update(delta: number) {
    if (this.enabled === false) return;
    // Change camera position
    if (this.heightSpeed) {
      var y = clamp(this.camera.position.y, this.heightMin, this.heightMax);
      var heightDelta = y - this.heightMin;
      this.autoSpeedFactor = delta * (heightDelta * this.heightCoef);
    } else {
      this.autoSpeedFactor = 0.0;
    }
    var actualMoveSpeed = delta * this.movementSpeed;
    if (this.moveForward || (this.autoForward && !this.moveBackward))
      this.camera.translateZ(-(actualMoveSpeed + this.autoSpeedFactor));
    if (this.moveBackward) this.camera.translateZ(actualMoveSpeed);
    if (this.moveLeft) this.camera.translateX(-actualMoveSpeed);
    if (this.moveRight) this.camera.translateX(actualMoveSpeed);
    if (this.moveUp) this.camera.translateY(actualMoveSpeed);
    if (this.moveDown) this.camera.translateY(-actualMoveSpeed);
    // Limit camera position
    this.limitCamera();
    // Look Speed
    var actualLookSpeed = delta * this.lookSpeed;
    if (!this.activeLook) actualLookSpeed = 0;
    var verticalLookRatio = 1;
    if (this.constrainVertical)
      verticalLookRatio = Math.PI / (this.verticalMax - this.verticalMin);
    this.lon += this.mouseX * actualLookSpeed;
    if (this.lookVertical)
      this.lat -= this.mouseY * actualLookSpeed * verticalLookRatio;
    this.lat = Math.max(-85, Math.min(85, this.lat));
    this.phi = degToRad(90 - this.lat);
    this.theta = degToRad(this.lon);
    // Limit Camera Look
    if (this.constrainVertical)
      this.phi = mapLinear(
        this.phi,
        0,
        Math.PI,
        this.verticalMin,
        this.verticalMax
      );
    // Set Camera Orientation
    const targetAffinityAmplitude = 1;
    var targetPosition = this.target,
      position = this.camera.position;
    targetPosition.x =
      position.x +
      targetAffinityAmplitude * Math.sin(this.phi) * Math.cos(this.theta);
    targetPosition.y =
      position.y + targetAffinityAmplitude * Math.cos(this.phi);
    targetPosition.z =
      position.z +
      targetAffinityAmplitude * Math.sin(this.phi) * Math.sin(this.theta);
    this.camera.lookAt(targetPosition);
  }

  limitCamera() {
    if (
      typeof this.maxClampPosition == "undefined" ||
      typeof this.minClampPosition == "undefined"
    )
      return;
    this.camera.position.setX(
      clamp(
        this.camera.position.x,
        this.minClampPosition.x,
        this.maxClampPosition.x
      )
    );
    this.camera.position.setY(
      clamp(
        this.camera.position.y,
        this.minClampPosition.y,
        this.maxClampPosition.y
      )
    );
    this.camera.position.setZ(
      clamp(
        this.camera.position.z,
        this.minClampPosition.z,
        this.maxClampPosition.z
      )
    );
  }

  onMouseUp(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (this.activeLook) {
      switch (event.button) {
        case 0:
          this.moveForward = false;
          break;
        case 2:
          this.moveBackward = false;
          break;
      }
    }

    this.mouseDragOn = false;
  }

  onMouseMove(event: MouseEvent) {
    this.mouseX = event.pageX - this.canvasElement.offsetLeft - this.viewHalfX;
    this.mouseY = event.pageY - this.canvasElement.offsetTop - this.viewHalfY;
    // console.log(`mouse move ${this.mouseX} ${this.mouseY}`)
  }

  onKeyDown(event: KeyboardEvent) {
    //event.preventDefault();

    switch (event.keyCode) {
      case 38: /*up*/
      case 87:
        /*W*/ this.moveForward = true;
        break;

      case 37: /*left*/
      case 65:
        /*A*/ this.moveLeft = true;
        break;

      case 40: /*down*/
      case 83:
        /*S*/ this.moveBackward = true;
        break;

      case 39: /*right*/
      case 68:
        /*D*/ this.moveRight = true;
        break;

      case 82:
        /*R*/ this.moveUp = true;
        break;
      case 70:
        /*F*/ this.moveDown = true;
        break;
    }
  }

  onKeyUp(event: KeyboardEvent) {
    switch (event.keyCode) {
      case 38: /*up*/
      case 87:
        /*W*/ this.moveForward = false;
        break;

      case 37: /*left*/
      case 65:
        /*A*/ this.moveLeft = false;
        break;

      case 40: /*down*/
      case 83:
        /*S*/ this.moveBackward = false;
        break;

      case 39: /*right*/
      case 68:
        /*D*/ this.moveRight = false;
        break;

      case 82:
        /*R*/ this.moveUp = false;
        break;
      case 70:
        /*F*/ this.moveDown = false;
        break;
    }
  }

  onMouseDown(event: MouseEvent) {
    this.canvasElement.focus();
    event.preventDefault();
    event.stopPropagation();

    if (this.activeLook) {
      switch (event.button) {
        case 0:
          this.moveForward = true;
          break;
        case 2:
          this.moveBackward = true;
          break;
      }
    }

    this.mouseDragOn = true;
  }

  handleResize() {
    this.viewHalfX = this.canvasElement.offsetWidth / 2;
    this.viewHalfY = this.canvasElement.offsetHeight / 2;
  }
}

export default FirstCameraControl;
